import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { ContentParser } from '@common/components/ContentParser';

import { SpaEntityError } from '@common/components/SpaEntity/SpaEntityError';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useCanonicalBookingStep, useLoadingState } from '@common/hooks';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import {
  useGetSpaFacilities,
  useGetSpaFacility,
  useGetSpaFacilityTimeslots,
  useGetSpaSettings,
} from '@features/spa/hooks';
import { useTranslations } from '@lang';
import {
  getDiscountForMinimumGuests,
  getPriceForMinimumGuests,
} from '@utils/getPriceForMinimumGuests';
import { hasAvailability, mergeCrossSellWithUpsell, parseDateToString } from '@utils/helpers';
import { isIntegrationUnavailable } from '@utils/integrationErrorCode';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import {
  mapSpaCrossSellToRecommendations,
  SpaCrossSellEntry,
} from '@utils/mapSpaCrossSellToRecommendations';
import { useRouter } from 'shared-routing';

import { cardAspectRatio, Recommendations, SpaTreatmentFacilityDetailTemplate } from 'voyager';

import { spaEntityFormSchema } from '@features/spa/pages/SpaTreatment/v2/SpaTreatmentForm.schema';
import { resolveSpaMaxGuests } from '@features/spa/utils/resolveSpaMaxGuests';
import { FacilityBookingV2 } from './FacilityBookingV2';
import { FacilityFormValues } from './FacilityBookingV2.types';
import { FacilityDetailV2Props } from './FacilityDetailV2.types';
import { FacilityExtrasV2 } from './FacilityExtrasV2';

export const FacilityDetailV2: FC<FacilityDetailV2Props> = ({
  id,
  bookingStep: requestedBookingStep,
  property,
  language,
  journeyParams,
  userHasAccessToFacilities,
  loggedInGuest,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const bookingStep = useCanonicalBookingStep(requestedBookingStep, {
    entity: 'facility',
    bookingRoute: Routes.FACILITY_BOOKING,
    detailRoute: Routes.FACILITY_DETAIL,
    id,
  });
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const { data: spaEntity, isFetching } = useGetSpaFacility(
    property.id,
    language,
    id,
    parseDateToString(journeyParams.spaDate),
    true,
    journeyParams.spaAdults
  );

  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, !!spaEntity);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const termsAndConditions = spaSettings?.termsAndConditions || '';
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const selectedDateString = moment(journeyParams.spaDate).format('YYYY-MM-DD');

  const {
    data: timeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
    error: timeslotsError,
    refetch: refetchTimeslots,
  } = useGetSpaFacilityTimeslots(
    property.id,
    id,
    selectedDateString,
    language,
    !!spaEntity && userHasAccessToFacilities,
    1,
    journeyParams.spaAdults
  );

  const { data: listedFacilities, isFetching: isFetchingListedAvailability } = useGetSpaFacilities(
    property.id,
    language,
    selectedDateString,
    null,
    0,
    true,
    journeyParams.spaAdults
  );

  const listedFacility = (Array.isArray(listedFacilities) ? listedFacilities : []).find(
    (facility) => String(facility.id) === String(id)
  );
  const isListedUnavailable =
    !isFetchingListedAvailability &&
    !!listedFacility &&
    !hasAvailability(listedFacility, selectedDateString);

  const isTelephoneOnly = spaEntity?.telephoneOnly || false;
  const isUnavailable =
    !isTelephoneOnly &&
    ((!isLoadingTimeslots &&
      (isTimeslotsError || (Array.isArray(timeslots) && timeslots.length === 0))) ||
      isListedUnavailable);

  // Distinct from "no availability": the read failed, so nothing is known either way. The listed
  // read is a separate answer — when it says unavailable we do know, so it keeps the sold-out copy.
  const integrationUnavailable = !isListedUnavailable && isIntegrationUnavailable(timeslotsError);

  const facilitiesVisibleToMembers =
    property?.modulesExcludingDisabled?.spa?.features?.facilities?.features?.visibleToMembers
      ?.enabled;

  const minGuests = spaEntity?.minimumGuests || 1;
  const requestedGuests = journeyParams.spaAdults || 1;
  const maxGuests = spaEntity ? resolveSpaMaxGuests(spaEntity, { requestedGuests }) : minGuests;
  const initialGuestCount = spaEntity
    ? Math.max(minGuests, Math.min(maxGuests, requestedGuests))
    : requestedGuests;

  const journeyBar = (
    <JourneyPickerV2Connected
      imageOverlay
      dateMode="single"
      dateContext="spa"
      showGuests
      maxGuests={spaEntity ? maxGuests : undefined}
      numberOfMonths={1}
      categories={navCategories}
      category="facilities"
      onCategoryChange={handleNavCategoryChange}
      disableCategorySelect
    />
  );

  const guestLimitNotice =
    spaEntity && requestedGuests > maxGuests
      ? t('spa.maximumGuestsNotice', {
          spaEntity: t('spa.facility').toLowerCase(),
          maxGuests,
        })
      : undefined;

  const form = useForm<FacilityFormValues>({
    resolver: zodResolver(spaEntityFormSchema),
    defaultValues: {
      date: moment(journeyParams.spaDate).format('YYYY-MM-DD'),
      guests: Array.from({ length: initialGuestCount }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender:
          spaEntity?.genderSpecific === 'M'
            ? 'male'
            : spaEntity?.genderSpecific === 'F'
            ? 'female'
            : '',
      })),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (form.getValues('date') !== selectedDateString) {
      form.setValue('date', selectedDateString);
    }
  }, [selectedDateString, form]);

  if (!userHasAccessToFacilities) {
    return (
      <SpaEntityError
        errorMessage={facilitiesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn'}
        values={{ spaEntity: 'facilities' }}
      />
    );
  }

  const handleBack = () => {
    router.push({ pathname: Routes.FACILITY_LIST });
  };

  const handleReserve = () => {
    router.push({
      pathname: Routes.FACILITY_BOOKING,
      query: { id, bookingStep: 'availability' },
    });
  };

  const handleCloseModal = (showMiniBasket = false) => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
    if (showMiniBasket) {
      setIsBasketDrawerOpen(true);
    }
  };

  const crossSellItems = mergeCrossSellWithUpsell(
    spaEntity?.upSell,
    'spa-facility',
    spaEntity?.crossSellObjects
  ) as SpaCrossSellEntry[];

  const { items: recommendations, targets: recommendationTargets } =
    mapSpaCrossSellToRecommendations(crossSellItems);

  const handleSelectRecommendation = (recId: string) => {
    const target = recommendationTargets.get(recId);
    if (target) {
      router.push({ pathname: target.route, query: { id: target.id } });
    }
  };

  const price = spaEntity
    ? getPriceForMinimumGuests(spaEntity.price, spaEntity.minimumGuests)
    : undefined;

  const discount = mapDiscountToDisplayObject(
    getDiscountForMinimumGuests(spaEntity?.discount, spaEntity?.minimumGuests ?? null)
  );

  const promotionName = spaEntity?.discount?.name;

  const durationLabel = spaEntity?.displayDuration ? `${spaEntity.displayDuration} min` : undefined;

  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  // One place decides which notice this page shows: "we could not check" when the read failed,
  // the sold-out copy only when we actually have an answer to report.
  const unavailableFormattedDate = moment(selectedDateString).format('dddd Do MMMM YYYY');
  const unavailableNotice = !isUnavailable
    ? undefined
    : integrationUnavailable
    ? {
        title: t('spa.package.integrationUnavailableHeading'),
        subtitle: t('spa.package.integrationUnavailableDescription', {
          title: spaEntity?.name,
          date: unavailableFormattedDate,
        }),
        ctaLabel: t('common.tryAgain'),
        onCtaClick: () => {
          void refetchTimeslots();
        },
      }
    : {
        title: t('common.unavailable'),
        subtitle: t('spa.package.unavailableDescription', {
          title: spaEntity?.name,
          date: unavailableFormattedDate,
        }),
        ctaLabel: t('common.backToModuleRoot', { entity: t('app.facilities') }),
        onCtaClick: handleBack,
      };

  return (
    <>
      {!bookingStep && (
        <>
          <Spinner showDelayMessage isActive={isRefetching} />
          <SpaTreatmentFacilityDetailTemplate
            imageUrl={spaEntity?.image?.url}
            imageAlt={spaEntity?.name}
            title={spaEntity?.name || ''}
            price={
              price?.amount != null
                ? { amount: price.amount, currencyCode: price.currencyCode }
                : undefined
            }
            discount={discount}
            priceLabel={t('common.from')}
            promotionName={promotionName}
            categoryLabel={durationLabel}
            description={spaEntity?.description}
            termsContent={
              spaEntity?.termsAndConditions || termsAndConditions ? (
                <ContentParser text={spaEntity?.termsAndConditions || termsAndConditions} />
              ) : undefined
            }
            termsLabel={t('app.termsAndConditions')}
            onReserve={
              spaEntity &&
              !isTelephoneOnly &&
              !isUnavailable &&
              !isRefetching &&
              !isLoadingTimeslots &&
              !isFetchingListedAvailability
                ? handleReserve
                : undefined
            }
            reserveLabel={t('spa.book')}
            onBack={handleBack}
            viewAllLabel={t('common.viewAll')}
            isLoading={isInitialLoading}
            journeyBar={journeyBar}
            guestLimitNotice={guestLimitNotice}
            telephoneOnly={
              isTelephoneOnly
                ? {
                    title: t('spa.telephoneOnly', {
                      spaEntity: t('spa.facility').toLowerCase(),
                    }),
                    subtitle: t('treatment.callUsOn', {
                      phoneNumber: spaSettings?.phoneNumber,
                    }),
                  }
                : undefined
            }
            unavailable={unavailableNotice}
            recommendations={
              recommendations.length > 0 ? (
                <Recommendations
                  items={recommendations}
                  onSelect={handleSelectRecommendation}
                  title={t('product.otherProducts.title')}
                  fromPriceLabel={t('common.from')}
                  noImageLabel={t('spa.noImage')}
                  visibleCount={{ mobile: 1.2, desktop: 3 }}
                  imageAspectRatio={cardAspectRatio.square.desktop}
                  imageMobileAspectRatio={cardAspectRatio.square.mobile}
                />
              ) : undefined
            }
          />
        </>
      )}
      {bookingStep === 'availability' && spaEntity && (
        <FormProvider {...form}>
          <FacilityBookingV2
            id={id}
            property={property}
            language={language}
            onClose={handleCloseModal}
            loggedInGuest={loggedInGuest}
          />
        </FormProvider>
      )}
      {bookingStep === 'extras' && spaEntity && (
        <FormProvider {...form}>
          <FacilityExtrasV2
            spaEntity={spaEntity}
            property={property}
            language={language}
            id={id}
            onClose={handleCloseModal}
            onBack={() =>
              router.push({
                pathname: Routes.FACILITY_BOOKING,
                query: { id, bookingStep: 'availability' },
              })
            }
          />
        </FormProvider>
      )}
      <BasketDrawerVersion
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </>
  );
};
